/**
 * Jupiter8Panel - Interface pour le Roland Jupiter-8
 * Design inspiré du vrai JP-8 avec ses couleurs caractéristiques
 */

import { themeManager } from '../themes/ThemeManager.js';
import { WaveSelector } from '../components/WaveSelector.js';
import { PresetSelector } from '../components/PresetSelector.js';
import { JUPITER8_PRESETS } from '../../state/InstrumentPresets.js';

export class Jupiter8Panel {
    constructor(jupiter8, store) {
        this.synth = jupiter8;
        this.store = store;
        this.panelId = 'jupiter8';
        this.components = {
            knobs: {},
            selects: {},
            stepButtons: []
        };
    }

    /**
     * Vérifie si le panel est collapsed
     */
    isCollapsed() {
        return this.store?.isPanelCollapsed(this.panelId) || false;
    }

    render(container) {
        this.container = container; // Sauvegarder pour re-render
        const isCollapsed = this.isCollapsed();
        const isPowered = this.store.getPowerState('jupiter8');

        this.synth.setPower(isPowered);

        container.innerHTML = `
            <div class="machine jupiter8 ${!isPowered ? 'powered-off' : ''} ${isCollapsed ? 'collapsed' : ''}" id="jupiter8_machine">
                <div class="machine-title jupiter8-title">
                    <button class="collapse-btn" id="jupiter8_collapse" title="Réduire/Agrandir">${isCollapsed ? '▶' : '▼'}</button>
                    ROLAND <span class="model">JUPITER-8</span>
                    <span class="brand">8-Voice Polyphonic Synthesizer</span>
                    <div id="jupiter8_presets"></div>
                    <button class="power-btn ${isPowered ? 'on' : ''}" id="jupiter8_power">⏻</button>
                </div>

                <div class="machine-content ${isCollapsed ? 'hidden' : ''}" id="jupiter8_content">
                <div class="jupiter8-controls">
                    <!-- VCO Section -->
                    <div class="jp8-section vco-section">
                        <div class="section-title">VCO-1</div>
                        <div class="controls">
                            <div id="jp8_vco1_wave"></div>
                            <div class="knob-group" id="jp8_vco1_oct"></div>
                            <div class="knob-group" id="jp8_vco1_level"></div>
                        </div>
                    </div>

                    <div class="jp8-section vco-section">
                        <div class="section-title">VCO-2</div>
                        <div class="controls">
                            <div id="jp8_vco2_wave"></div>
                            <div class="knob-group" id="jp8_vco2_oct"></div>
                            <div class="knob-group" id="jp8_vco2_level"></div>
                            <div class="knob-group" id="jp8_vco2_detune"></div>
                        </div>
                    </div>

                    <div class="jp8-section mixer-section">
                        <div class="section-title">MIXER</div>
                        <div class="controls">
                            <div class="knob-group" id="jp8_sub"></div>
                            <div class="knob-group" id="jp8_noise"></div>
                        </div>
                    </div>

                    <!-- Filter Section -->
                    <div class="jp8-section filter-section">
                        <div class="section-title">VCF</div>
                        <div class="controls">
                            <div class="knob-group" id="jp8_cutoff"></div>
                            <div class="knob-group" id="jp8_resonance"></div>
                            <div class="knob-group" id="jp8_env_amt"></div>
                            <div class="knob-group" id="jp8_hpf"></div>
                        </div>
                    </div>

                    <!-- Filter Envelope -->
                    <div class="jp8-section env-section">
                        <div class="section-title">VCF ENV</div>
                        <div class="controls">
                            <div class="knob-group" id="jp8_fenv_a"></div>
                            <div class="knob-group" id="jp8_fenv_d"></div>
                            <div class="knob-group" id="jp8_fenv_s"></div>
                            <div class="knob-group" id="jp8_fenv_r"></div>
                        </div>
                    </div>

                    <!-- Amp Envelope -->
                    <div class="jp8-section env-section">
                        <div class="section-title">VCA ENV</div>
                        <div class="controls">
                            <div class="knob-group" id="jp8_aenv_a"></div>
                            <div class="knob-group" id="jp8_aenv_d"></div>
                            <div class="knob-group" id="jp8_aenv_s"></div>
                            <div class="knob-group" id="jp8_aenv_r"></div>
                        </div>
                    </div>

                    <!-- LFO Section -->
                    <div class="jp8-section lfo-section">
                        <div class="section-title">LFO</div>
                        <div class="controls">
                            <div class="knob-group" id="jp8_lfo_rate"></div>
                            <div class="knob-group" id="jp8_lfo_pitch"></div>
                            <div class="knob-group" id="jp8_lfo_filter"></div>
                        </div>
                    </div>

                    <!-- Chorus Section -->
                    <div class="jp8-section chorus-section">
                        <div class="section-title">CHORUS</div>
                        <div class="controls">
                            <div class="knob-group" id="jp8_chorus_rate"></div>
                            <div class="knob-group" id="jp8_chorus_depth"></div>
                            <div class="knob-group" id="jp8_chorus_mix"></div>
                        </div>
                    </div>

                    <!-- Master -->
                    <div class="jp8-section master-section">
                        <div class="section-title">MASTER</div>
                        <div class="controls">
                            <div class="knob-group" id="jp8_volume"></div>
                            <div class="knob-group" id="jp8_porta"></div>
                        </div>
                    </div>
                </div>

                <!-- Sequencer -->
                <div class="sequencer machine-sequencer">
                    <div class="step-markers">
                        ${[...Array(16)].map((_, i) => `<span class="step-marker">${i + 1}</span>`).join('')}
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">GATE</span>
                        <div class="steps" id="jupiter8_gates"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">NOTE</span>
                        <div class="note-row" id="jupiter8_notes"></div>
                    </div>
                </div>
                </div><!-- /machine-content -->
            </div><!-- /machine -->
        `;

        this.setupCollapseButton();
        this.setupPowerButton();

        // Si collapsed, on ne crée pas les contrôles (optimisation)
        if (!isCollapsed) {
            this.setupPresetSelector();
            this.createVcoControls();
            this.createFilterControls();
            this.createEnvelopeControls();
            this.createLfoControls();
            this.createChorusControls();
            this.createMasterControls();
            this.createSequencer();
            this._loadParams();
        }
    }

    /**
     * Configure le bouton collapse
     */
    setupCollapseButton() {
        const collapseBtn = document.getElementById('jupiter8_collapse');
        if (!collapseBtn) return;

        collapseBtn.addEventListener('click', () => {
            this.store.togglePanelCollapsed(this.panelId);

            // Re-render le panel
            this.destroy();
            this.render(this.container);
        });
    }

    setupPowerButton() {
        const powerBtn = document.getElementById('jupiter8_power');
        const machine = document.getElementById('jupiter8_machine');

        powerBtn.addEventListener('click', () => {
            const newState = !this.synth.getPower();
            this.synth.setPower(newState);
            this.store.setPowerState('jupiter8', newState);
            powerBtn.classList.toggle('on', newState);
            machine.classList.toggle('powered-off', !newState);
        });
    }

    createVcoControls() {
        // VCO1 Wave
        const vco1WaveContainer = document.getElementById('jp8_vco1_wave');
        this.components.vco1Wave = new WaveSelector(vco1WaveContainer, {
            waveforms: [
                { value: 'sawtooth', label: 'SAW' },
                { value: 'square', label: 'SQR' },
                { value: 'triangle', label: 'TRI' },
                { value: 'sine', label: 'SIN' }
            ],
            value: this.synth._params.vco1Wave
        }).onChange(wave => {
            this.synth.setVco1Wave(wave);
            this._saveParams();
        });

        // VCO1 Octave
        this.components.knobs.vco1Oct = themeManager.createKnob(
            document.getElementById('jp8_vco1_oct'), {
                min: -2, max: 2, value: this.synth._params.vco1Octave,
                label: 'OCT', step: 1
            }
        ).onChange(value => {
            this.synth.setVco1Octave(value);
            this._saveParams();
        });

        // VCO1 Level
        this.components.knobs.vco1Level = themeManager.createKnob(
            document.getElementById('jp8_vco1_level'), {
                min: 0, max: 1, value: this.synth._params.vco1Level,
                label: 'LEVEL', step: 0.01
            }
        ).onChange(value => {
            this.synth.setVco1Level(value);
            this._saveParams();
        });

        // VCO2 Wave
        const vco2WaveContainer = document.getElementById('jp8_vco2_wave');
        this.components.vco2Wave = new WaveSelector(vco2WaveContainer, {
            waveforms: [
                { value: 'sawtooth', label: 'SAW' },
                { value: 'square', label: 'SQR' },
                { value: 'triangle', label: 'TRI' },
                { value: 'sine', label: 'SIN' }
            ],
            value: this.synth._params.vco2Wave
        }).onChange(wave => {
            this.synth.setVco2Wave(wave);
            this._saveParams();
        });

        // VCO2 Octave
        this.components.knobs.vco2Oct = themeManager.createKnob(
            document.getElementById('jp8_vco2_oct'), {
                min: -2, max: 2, value: this.synth._params.vco2Octave,
                label: 'OCT', step: 1
            }
        ).onChange(value => {
            this.synth.setVco2Octave(value);
            this._saveParams();
        });

        // VCO2 Level
        this.components.knobs.vco2Level = themeManager.createKnob(
            document.getElementById('jp8_vco2_level'), {
                min: 0, max: 1, value: this.synth._params.vco2Level,
                label: 'LEVEL', step: 0.01
            }
        ).onChange(value => {
            this.synth.setVco2Level(value);
            this._saveParams();
        });

        // VCO2 Detune
        this.components.knobs.vco2Detune = themeManager.createKnob(
            document.getElementById('jp8_vco2_detune'), {
                min: -50, max: 50, value: this.synth._params.vco2Detune,
                label: 'DETUNE', step: 1, unit: 'ct'
            }
        ).onChange(value => {
            this.synth.setVco2Detune(value);
            this._saveParams();
        });

        // Sub Level
        this.components.knobs.subLevel = themeManager.createKnob(
            document.getElementById('jp8_sub'), {
                min: 0, max: 1, value: this.synth._params.subLevel,
                label: 'SUB', step: 0.01
            }
        ).onChange(value => {
            this.synth.setSubLevel(value);
            this._saveParams();
        });

        // Noise Level
        this.components.knobs.noiseLevel = themeManager.createKnob(
            document.getElementById('jp8_noise'), {
                min: 0, max: 1, value: this.synth._params.noiseLevel,
                label: 'NOISE', step: 0.01
            }
        ).onChange(value => {
            this.synth.setNoiseLevel(value);
            this._saveParams();
        });
    }

    createFilterControls() {
        this.components.knobs.cutoff = themeManager.createKnob(
            document.getElementById('jp8_cutoff'), {
                min: 20, max: 20000, value: this.synth._params.filterCutoff,
                label: 'FREQ', step: 1, unit: 'Hz'
            }
        ).onChange(value => {
            this.synth.setFilterCutoff(value);
            this._saveParams();
        });

        this.components.knobs.resonance = themeManager.createKnob(
            document.getElementById('jp8_resonance'), {
                min: 0, max: 20, value: this.synth._params.filterResonance,
                label: 'RES', step: 0.1
            }
        ).onChange(value => {
            this.synth.setFilterResonance(value);
            this._saveParams();
        });

        this.components.knobs.envAmt = themeManager.createKnob(
            document.getElementById('jp8_env_amt'), {
                min: 0, max: 1, value: this.synth._params.filterEnvAmount,
                label: 'ENV', step: 0.01
            }
        ).onChange(value => {
            this.synth.setFilterEnvAmount(value);
            this._saveParams();
        });

        this.components.knobs.hpf = themeManager.createKnob(
            document.getElementById('jp8_hpf'), {
                min: 20, max: 2000, value: this.synth._params.hpfCutoff,
                label: 'HPF', step: 1, unit: 'Hz'
            }
        ).onChange(value => {
            this.synth.setHpfCutoff(value);
            this._saveParams();
        });
    }

    createEnvelopeControls() {
        // Filter Envelope
        const fenvParams = [
            { id: 'jp8_fenv_a', param: 'filterAttack', label: 'A', setter: 'setFilterAttack' },
            { id: 'jp8_fenv_d', param: 'filterDecay', label: 'D', setter: 'setFilterDecay' },
            { id: 'jp8_fenv_s', param: 'filterSustain', label: 'S', setter: 'setFilterSustain' },
            { id: 'jp8_fenv_r', param: 'filterRelease', label: 'R', setter: 'setFilterRelease' }
        ];

        fenvParams.forEach(p => {
            const isTime = p.label !== 'S';
            this.components.knobs[p.param] = themeManager.createKnob(
                document.getElementById(p.id), {
                    min: isTime ? 0.001 : 0,
                    max: isTime ? 4 : 1,
                    value: this.synth._params[p.param],
                    label: p.label, step: 0.01
                }
            ).onChange(value => {
                this.synth[p.setter](value);
                this._saveParams();
            });
        });

        // Amp Envelope
        const aenvParams = [
            { id: 'jp8_aenv_a', param: 'ampAttack', label: 'A', setter: 'setAmpAttack' },
            { id: 'jp8_aenv_d', param: 'ampDecay', label: 'D', setter: 'setAmpDecay' },
            { id: 'jp8_aenv_s', param: 'ampSustain', label: 'S', setter: 'setAmpSustain' },
            { id: 'jp8_aenv_r', param: 'ampRelease', label: 'R', setter: 'setAmpRelease' }
        ];

        aenvParams.forEach(p => {
            const isTime = p.label !== 'S';
            this.components.knobs[p.param] = themeManager.createKnob(
                document.getElementById(p.id), {
                    min: isTime ? 0.001 : 0,
                    max: isTime ? 4 : 1,
                    value: this.synth._params[p.param],
                    label: p.label, step: 0.01
                }
            ).onChange(value => {
                this.synth[p.setter](value);
                this._saveParams();
            });
        });
    }

    createLfoControls() {
        this.components.knobs.lfoRate = themeManager.createKnob(
            document.getElementById('jp8_lfo_rate'), {
                min: 0.1, max: 20, value: this.synth._params.lfoRate,
                label: 'RATE', step: 0.1, unit: 'Hz'
            }
        ).onChange(value => {
            this.synth.setLfoRate(value);
            this._saveParams();
        });

        this.components.knobs.lfoToPitch = themeManager.createKnob(
            document.getElementById('jp8_lfo_pitch'), {
                min: 0, max: 1, value: this.synth._params.lfoToPitch,
                label: 'PITCH', step: 0.01
            }
        ).onChange(value => {
            this.synth.setLfoToPitch(value);
            this._saveParams();
        });

        this.components.knobs.lfoToFilter = themeManager.createKnob(
            document.getElementById('jp8_lfo_filter'), {
                min: 0, max: 1, value: this.synth._params.lfoToFilter,
                label: 'VCF', step: 0.01
            }
        ).onChange(value => {
            this.synth.setLfoToFilter(value);
            this._saveParams();
        });
    }

    createChorusControls() {
        this.components.knobs.chorusRate = themeManager.createKnob(
            document.getElementById('jp8_chorus_rate'), {
                min: 0.1, max: 5, value: this.synth._params.chorusRate,
                label: 'RATE', step: 0.1
            }
        ).onChange(value => {
            this.synth.setChorusRate(value);
            this._saveParams();
        });

        this.components.knobs.chorusDepth = themeManager.createKnob(
            document.getElementById('jp8_chorus_depth'), {
                min: 0, max: 1, value: this.synth._params.chorusDepth,
                label: 'DEPTH', step: 0.01
            }
        ).onChange(value => {
            this.synth.setChorusDepth(value);
            this._saveParams();
        });

        this.components.knobs.chorusMix = themeManager.createKnob(
            document.getElementById('jp8_chorus_mix'), {
                min: 0, max: 1, value: this.synth._params.chorusMix,
                label: 'MIX', step: 0.01
            }
        ).onChange(value => {
            this.synth.setChorusMix(value);
            this._saveParams();
        });
    }

    createMasterControls() {
        this.components.knobs.volume = themeManager.createKnob(
            document.getElementById('jp8_volume'), {
                min: 0, max: 1, value: this.synth._params.volume,
                label: 'VOLUME', step: 0.01
            }
        ).onChange(value => {
            this.synth.setVolume(value);
            this._saveParams();
        });

        this.components.knobs.portamento = themeManager.createKnob(
            document.getElementById('jp8_porta'), {
                min: 0, max: 1, value: this.synth._params.portamento,
                label: 'PORTA', step: 0.01
            }
        ).onChange(value => {
            this.synth.setPortamento(value);
            this._saveParams();
        });
    }

    createSequencer() {
        const notesContainer = document.getElementById('jupiter8_notes');
        const gatesContainer = document.getElementById('jupiter8_gates');

        // Note selectors
        for (let i = 0; i < 16; i++) {
            this.components.selects[`note_${i}`] = themeManager.createSelect(notesContainer, {
                options: this.store.noteOptions,
                value: this.store.patterns.jupiter8Notes ? this.store.patterns.jupiter8Notes[i] : 'C3',
                className: 'note-select'
            }).onChange(value => {
                this.store.setStep('jupiter8Notes', i, value);
            });
        }

        // Gate buttons
        for (let i = 0; i < 16; i++) {
            const stepBtn = themeManager.createStepButton(gatesContainer, {
                index: i,
                type: 'gate',
                active: this.store.patterns.jupiter8Gates ? this.store.patterns.jupiter8Gates[i] : false
            }).onChange(active => {
                this.store.setStep('jupiter8Gates', i, active);
            });

            this.components.stepButtons.push(stepBtn);
        }
    }

    setupPresetSelector() {
        const container = document.getElementById('jupiter8_presets');
        if (!container) return;

        this.presetSelector = new PresetSelector({
            presets: JUPITER8_PRESETS,
            currentPreset: 'Init',
            onChange: (name, data) => this._loadPreset(data)
        });

        container.appendChild(this.presetSelector.element);
    }

    _loadPreset(presetData) {
        if (!presetData) return;

        this.synth.setParams(presetData);
        this._updateKnobsFromParams();
        this._saveParams();
    }

    _updateKnobsFromParams() {
        const params = this.synth.getParams();
        Object.entries(this.components.knobs).forEach(([key, knob]) => {
            if (params[key] !== undefined) {
                knob.setValue(params[key]);
            }
        });

        // Update wave selectors
        if (this.components.vco1Wave) {
            this.components.vco1Wave.setValue(params.vco1Wave);
        }
        if (this.components.vco2Wave) {
            this.components.vco2Wave.setValue(params.vco2Wave);
        }
    }

    _saveParams() {
        this.store?.setSynthParams('jupiter8', this.synth.getParams());
    }

    _loadParams() {
        const saved = this.store?.getSynthParams('jupiter8');
        if (saved) {
            this.synth.setParams(saved);
            this._updateKnobsFromParams();
        }
    }

    destroy() {
        Object.values(this.components.knobs).forEach(k => k?.destroy?.());
        Object.values(this.components.selects).forEach(s => s?.destroy?.());
        this.components.stepButtons.forEach(b => b?.destroy?.());
        this.components.vco1Wave?.destroy?.();
        this.components.vco2Wave?.destroy?.();
    }
}
